import {
  IonButton,
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
} from "@ionic/react";
import PageHeader from "../components/PageHeader";
import { useParams } from "react-router";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  Equipment,
  EquipmentHierarchyChildType,
  EquipmentHierarchyType,
} from "../models/equipment/Equipment";
import { getEquipmentHierarchyList } from "../api/EqupimentHierarchies";
import { getEquipment } from "../api/EquipmentMasters";
import { TranslationsContext } from "../util/Translations";
import "./EquipmentHierarchy.css";
import {
  appsSharp,
  barcodeOutline,
  chevronDown,
  chevronForward,
  cogOutline,
  cubeOutline,
  listOutline,
  pricetagOutline,
} from "ionicons/icons";
import EquipmentHierarchyPartActionBar from "./EquipmentHierarchyPartActionBar";
import {
  getUserRequesterAccess,
  getUserTechnicianAccess,
} from "../util/UserHelper";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";

type TreeNodeProps = {
  node: TreeNodeInfo;
  level?: number; // Added to track depth for spacing
  displayEquipment: string;
  onSelect?: (node: TreeNodeInfo) => void;
  selectedNode?: TreeNodeInfo;
};

export type TreeNodeInfo = {
  part: string;
  description: string;
  objectId: string;
  levelNumber: number;
  partType?: number;
  quantityUsed?: number;
  children?: TreeNodeInfo[];
  icon?: string; // Custom icon for each node
};

function getIcon(partType: number) {
  switch (partType) {
    case 1:
      return cogOutline;
    case 2:
      return barcodeOutline;
    case 3:
      return listOutline;
    case 4:
      return pricetagOutline;
    case 5:
      return cubeOutline;
    default:
      return cogOutline;
  }
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  level = 0,
  displayEquipment,
  onSelect,
  selectedNode,
}) => {
  const { description, part, children, partType, quantityUsed } = node;
  const { translations } = useContext(TranslationsContext);

  const [expanded, setExpanded] = useState(false);

  const toggleExpand = () => {
    if (children && children.length > 0) {
      setExpanded(!expanded);
    }
  };

  const handleSelect = () => {
    onSelect?.(node);
  };

  const label = `${part}${description && ` (${description})`}`;
  const link = `/Equipment/${encodeParam(part)}/hierarchy`;
  const detailsLink = `/Equipment/${encodeParam(part)}`;
  const detailsButton = (
    <IonButton routerLink={detailsLink} fill="clear">
      {`${translations["lbl_btn_details"] || "Details"}`}
    </IonButton>
  );

  return (
    <>
      <IonItem
        button={!!children?.length} // Only make clickable if expandable
        onClick={() => {
          handleSelect();
          toggleExpand();
        }}
        lines="none" // Removes extra divider lines for a cleaner look
        style={{
          paddingLeft: `${level * 24}px`, // Even tighter indentation
          minHeight: "18px", // Minimize row height
          "--padding-start": "4px", // Reduce left padding
          "--inner-padding-end": "0px", // Reduce right padding
          "--background":
            selectedNode?.objectId === node.objectId
              ? "var(--ion-color-light)"
              : "transparent",
        }}
      >
        {/* Expand/Collapse Icon */}
        {children && children.length > 0 ? (
          <IonIcon
            slot="start"
            icon={expanded ? chevronDown : chevronForward}
            style={{ fontSize: "18px", marginRight: "4px" }}
          />
        ) : (
          <div style={{ width: "18px" }} /> // Alignment placeholder
        )}
        <div style={{ minHeight: "18px", minWidth: "18px" }}>
          {/* Custom Middle Icon */}
          <IonIcon
            icon={node.icon || cogOutline} // Default icon if none provided
            style={{ fontSize: "18px", marginRight: "5px", color: "#555" }}
          />
        </div>

        {/* Node */}
        {partType === 1 && (
          <>
            <IonButton
              fill="clear"
              routerLink={part !== displayEquipment ? link : undefined}
              style={{ maxWidth: "70%" }}
            >
              <IonLabel class="hierarchy-label">{label}</IonLabel>
            </IonButton>
            {" - "}
            {detailsButton}
          </>
        )}
        {partType !== 1 &&
          (partType === 5 || partType === 3 || partType === 4 ? (
            <IonLabel>
              {label} {` (${quantityUsed})`}
            </IonLabel>
          ) : (
            // The original code runs when partType is not 1 and not 5
            <IonLabel>{label}</IonLabel>
          ))}
      </IonItem>
      {expanded && children && (
        <IonList style={{ padding: "0" }}>
          {children.map((child) => (
            <TreeNode
              key={child.objectId}
              node={child}
              level={level + 1}
              displayEquipment={displayEquipment}
              onSelect={onSelect}
              selectedNode={selectedNode}
            />
          ))}
        </IonList>
      )}
    </>
  );
};

const EquipmentHierarchy: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const displayEquipment = decodeParam(equipmentId);
  const [hierarchy, setHierarchy] = useState<EquipmentHierarchyType>();
  const [showLoading, setShowLoading] = useState(false);
  const [equipment, setEquipment] = useState<Equipment>();
  const [eqpList, setEqpList] = useState<EquipmentHierarchyChildType[]>([]);
  const [treeNode, setTreeNode] = useState<TreeNodeInfo | undefined>(undefined);
  const [selectedNode, setSelectedNode] = useState<TreeNodeInfo | undefined>(
    undefined
  );
  const [isRequester, setIsRequester] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const getHierarchy = async (part: string) => {
    const params = {
      equipment: part,
    };
    const response = await getEquipmentHierarchyList(params);
    return response.data as EquipmentHierarchyType;
  };

  const getEquipmentDetails = async (name: string) => {
    const response = await getEquipment({ equipment: name });
    return response.data as Equipment;
  };

  const setup = useCallback(async () => {
    setShowLoading(true);
    const [hierarchyDetails, equipment] = await Promise.all([
      getHierarchy(displayEquipment),
      getEquipmentDetails(displayEquipment),
    ]);
    setHierarchy(hierarchyDetails);
    setEquipment(equipment);
    setEqpList(hierarchyDetails.eqpList || []);
    if (
      !(await getUserTechnicianAccess()) &&
      (await getUserRequesterAccess())
    ) {
      setIsRequester(true);
    }
    setShowLoading(false);
  }, [displayEquipment]);

  useEffect(() => {
    setHierarchy(undefined);
    setEquipment(undefined);
    setup();
  }, [displayEquipment, setup]);

  const buildTreeNode = useCallback(
    (info: TreeNodeInfo) => {
      const {
        description,
        levelNumber,
        objectId,
        part,
        icon,
        partType,
        quantityUsed,
      } = info;
      const children = eqpList.filter((eqp) => {
        return (
          (eqp?.Parent || "").trim() === part &&
          (eqp?.ParentDescription || "").trim() === description &&
          eqp?.LevelNumber === levelNumber
        );
      });
      const treeNode: TreeNodeInfo = {
        description,
        levelNumber,
        objectId,
        part,
        children: children.map((c) =>
          buildTreeNode({
            description: (c.PartDescription ?? "").trim(),
            levelNumber: c.LevelNumber + 1,
            objectId: c.ObjectId,
            part: (c.Part ?? "").trim(),
            partType: c.PartType,
            quantityUsed: c.QuantityUsed,
            icon: getIcon(c.PartType),
          })
        ),
        icon: icon ?? (partType ? getIcon(partType) : ""),
        partType,
        quantityUsed,
      };
      return treeNode;
    },
    [eqpList]
  );

  useEffect(() => {
    if (hierarchy && equipment) {
      const node = buildTreeNode({
        part: displayEquipment.trim(),
        description: (equipment?.Description || "").trim(),
        objectId: displayEquipment,
        levelNumber: 1,
      });
      setTreeNode(node);
    }
  }, [hierarchy, equipment, buildTreeNode, displayEquipment]);

  //actions are available only for Equipment (part_type = 1 || part_type = undefined) & Inventory (part_type = 5)
  const showActions = !!(
    selectedNode &&
    (selectedNode.partType === 1 ||
      selectedNode.partType === 5 ||
      selectedNode.partType === undefined)
  );

  return (
    <IonPage>
      <IonLoading isOpen={showLoading} />
      <PageHeader title={displayEquipment} showBack />
      <IonContent class="ion-padding">
        <h3>{`${translations["lbl_hierarchy"] || "Hierarchy"}`}</h3>
        {treeNode && (
          <TreeNode
            node={treeNode}
            displayEquipment={displayEquipment}
            onSelect={(node) => setSelectedNode(node)}
            selectedNode={selectedNode}
          />
        )}
        {/* Floating Action Button (FAB) - Visible only when a node is selected */}
        {showActions && (
          <>
            <IonFab slot="fixed" vertical="bottom" horizontal="end">
              <IonFabButton onClick={() => setIsOpen(true)}>
                <IonIcon icon={appsSharp}></IonIcon>
              </IonFabButton>
            </IonFab>
            <EquipmentHierarchyPartActionBar
              isOpen={isOpen}
              onDismiss={() => setIsOpen(false)}
              partType={selectedNode.partType}
              isRequester={isRequester}
              equipmentId={selectedNode.part}
              part={selectedNode.part}
            />
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default withAITracking(
  reactPlugin,
  EquipmentHierarchy,
  "EquipmentHierarchy"
);
